// frontend/src/components/calendar/CalendarEditor.jsx

import { useState, useEffect, useMemo, useCallback, useRef, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import {
  TextField,
  FormControlLabel,
  RadioGroup,
  Radio,
  FormControl,
  FormLabel,
} from '@mui/material';
import { lengthLimits } from '../../constants/orgFieldLimits';
import { WEEKDAY_KEYS, formatWeekdayShortLabels } from './workingDayResolver';
import { CALENDAR_NAME_MAX } from './newCalendar';
import '../../styles/calendar.css';

// See lengthLimits.
const CALENDAR_FIELD_MAX = { calendar_name: CALENDAR_NAME_MAX };

const CalendarEditor = forwardRef(({ calendar, onUpdate, onStatusChange, onPatternChange, onNameChange }, ref) => {
  const { t, i18n } = useTranslation();
  const nameInputRef = useRef(null);
  const [formData, setFormData] = useState({
    calendar_name: '',
    description: '',
    working_days_pattern: {},
    closed_day_delivery_rule: 'NEXT',
    is_active: true,
  });
  const [originalData, setOriginalData] = useState(null);

  useEffect(() => {
    if (calendar) {
      const data = {
        calendar_name: calendar.calendar_name || '',
        description: calendar.description || '',
        working_days_pattern: calendar.working_days_pattern || {},
        closed_day_delivery_rule: calendar.closed_day_delivery_rule || 'NEXT',
        is_active: calendar.is_active !== undefined ? calendar.is_active : true,
      };
      setFormData(data);
      setOriginalData(data);
    }
  }, [calendar]);

  const isDirty = useMemo(() => {
    if (!originalData) return false;
    return JSON.stringify(formData) !== JSON.stringify(originalData);
  }, [formData, originalData]);

  const lengths = lengthLimits(formData, CALENDAR_FIELD_MAX, t);
  const canSave = isDirty && !lengths.tooLong;

  // The host renders Save and Discard: Discard needs only a change, Save also
  // needs the change to be one the server accepts.
  useEffect(() => {
    onStatusChange({ dirty: isDirty, canSave });
  }, [isDirty, canSave, onStatusChange]);

  useEffect(() => {
    if (onPatternChange) onPatternChange(formData.working_days_pattern);
  }, [formData.working_days_pattern, onPatternChange]);

  useEffect(() => {
    if (onNameChange) onNameChange(formData.calendar_name);
  }, [formData.calendar_name, onNameChange]);

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleDayToggle = (day) => {
    setFormData({
      ...formData,
      working_days_pattern: {
        ...formData.working_days_pattern,
        [day]: !formData.working_days_pattern[day],
      },
    });
  };

  const save = useCallback(() => {
    onUpdate({ ...calendar, ...formData });
  }, [onUpdate, calendar, formData]);

  const discard = useCallback(() => {
    if (originalData) {
      setFormData({ ...originalData });
    }
  }, [originalData]);

  const focusName = useCallback(() => {
    // The TextField renders its <input> inside a wrapper; reach the inner
    // input to focus + select so the merchant can immediately retype.
    const el = nameInputRef.current;
    if (!el) return;
    const inputEl = el.querySelector?.('input') || el;
    inputEl.focus?.();
    inputEl.select?.();
    inputEl.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  }, []);

  useImperativeHandle(ref, () => ({ save, discard, focusName }), [save, discard, focusName]);

  const dayLabels = formatWeekdayShortLabels(i18n.language);

  return (
    <div className="calendar-editor">
      {/* The host's header shows the name as display text plus a pencil
          affordance that calls ``focusName()`` on this field. */}
      <TextField
        inputRef={nameInputRef}
        label={t('calendar.editor.nameLabel')}
        value={formData.calendar_name}
        onChange={(e) => handleInputChange('calendar_name', e.target.value)}
        fullWidth
        size="small"
        className="calendar-field"
        {...lengths.propsFor('calendar_name')}
      />

      {/* Working Days */}
      <div className="calendar-section">
        <div className="calendar-section-title">{t('calendar.editor.workingDays')}</div>
        <div className="calendar-days-grid">
          {WEEKDAY_KEYS.map((day, i) => (
            <div
              key={day}
              className={`calendar-day-card ${formData.working_days_pattern[day] ? 'working-day' : 'non-working-day'}`}
              onClick={() => handleDayToggle(day)}
            >
              <div className="calendar-day-label">{dayLabels[i]}</div>
              <div className="calendar-day-status">
                {formData.working_days_pattern[day] ? '✓' : '×'}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Delivery Rule */}
      <FormControl component="fieldset" className="calendar-field">
        <FormLabel component="legend">{t('calendar.editor.closedDayDelivery')}</FormLabel>
        <RadioGroup
          value={formData.closed_day_delivery_rule}
          onChange={(e) => handleInputChange('closed_day_delivery_rule', e.target.value)}
        >
          <FormControlLabel
            value="EARLY"
            control={<Radio />}
            label={t('calendar.editor.deliverEarly')}
          />
          <FormControlLabel
            value="NEXT"
            control={<Radio />}
            label={t('calendar.editor.deliverNext')}
          />
        </RadioGroup>
      </FormControl>
    </div>
  );
});

CalendarEditor.displayName = 'CalendarEditor';

export default CalendarEditor;
